import { SurveyBuilderConstants as Constants } from 'components/survey_builder';

import { SkipLogicConditionUtilities } from './skip_logic_condition_utilities';
import { Answer, Question, Survey } from './types';
import {
  buildAnswer,
  insertIntoArray,
  removeFromArray,
  sortAnswerIds,
} from './utilities';

const addAnswer = (
  questionUUID: string,
  prevAnswerUUID: string,
  survey: Survey,
) => {
  const question = { ...survey.questions[questionUUID] } as Question;
  const newAnswer = buildAnswer(question.pick);
  const answers = { ...survey.answers, [newAnswer.uuid]: newAnswer };

  question.answerIds = sortAnswerIds(
    insertIntoArray(question.answerIds, newAnswer.uuid, prevAnswerUUID),
    answers,
  );

  return {
    answers,
    questions: { ...survey.questions, [question.uuid]: question },
  };
};

const addAnswers = (
  questionUUID: string,
  answerUUID: string,
  answers: string[],
  survey: Survey,
) => {
  const question = { ...survey.questions[questionUUID] } as Question;

  let currentPrevAnswerUUID: string;

  const newAnswers: { [key: string]: Answer } = {};

  answers
    .filter((answer) => !!answer)
    .forEach((answer, index) => {
      if (index === 0) {
        currentPrevAnswerUUID = answerUUID;
        newAnswers[answerUUID] = {
          ...survey.answers[answerUUID],
          text: answer,
        };
      } else {
        const newAnswer = buildAnswer(question.pick);
        question.answerIds = insertIntoArray(
          question.answerIds,
          newAnswer.uuid,
          currentPrevAnswerUUID,
        );
        currentPrevAnswerUUID = newAnswer.uuid;
        newAnswers[newAnswer.uuid] = { ...newAnswer, text: answer };
      }
    });

  const updatedAnswers = { ...survey.answers, ...newAnswers };
  question.answerIds = sortAnswerIds(question.answerIds, updatedAnswers);

  return {
    answers: updatedAnswers,
    questions: { ...survey.questions, [question.uuid]: question },
  };
};

const addOtherAnswer = (questionUUID: string, survey: Survey) => {
  const question = { ...survey.questions[questionUUID] } as Question;
  const newAnswer = buildAnswer(question.pick, {
    responseClass: Constants.RESPONSE_CLASS_OTHER_AND_STRING,
    text: 'Other',
  });

  const updatedQuestion = {
    ...question,
    answerIds: sortAnswerIds(
      insertIntoArray(question.answerIds, newAnswer.uuid),
      { ...survey.answers, [newAnswer.uuid]: newAnswer },
    ),
  };

  return {
    answers: { ...survey.answers, [newAnswer.uuid]: newAnswer },
    questions: { ...survey.questions, [question.uuid]: updatedQuestion },
  };
};

const moveAnswer = (
  questionId: string,
  activeAnswerId: string,
  overAnswerId: string,
  survey: Survey,
) => {
  const question = { ...survey.questions[questionId] } as Question;

  const answerIds = [...question.answerIds];
  const currentPosition = answerIds.indexOf(activeAnswerId);
  const targetPosition = answerIds.indexOf(overAnswerId);
  if (currentPosition === targetPosition) return survey;

  answerIds.splice(currentPosition, 1);
  answerIds.splice(targetPosition, 0, activeAnswerId);

  return {
    answers: survey.answers,
    questions: {
      ...survey.questions,
      [questionId]: {
        ...survey.questions[questionId],
        answerIds: sortAnswerIds(answerIds, survey.answers),
      },
    },
  };
};

const removeAnswer = (
  questionUUID: string,
  answerUUID: string,
  survey: Survey,
) => {
  const question = { ...survey.questions[questionUUID] } as Question;

  if (question.answerIds.length === 1) return survey;

  const answers = { ...survey.answers };
  delete answers[answerUUID];

  question.answerIds = sortAnswerIds(
    removeFromArray(question.answerIds, answerUUID),
    answers,
  );

  const updatedSurvey = {
    ...survey,
    answers,
    questions: {
      ...survey.questions,
      [questionUUID]: question,
    },
  };

  // Remove any connected skip logic conditions
  const skipLogicUpdates = SkipLogicConditionUtilities.removeAnswerConditions(
    questionUUID,
    answerUUID,
    updatedSurvey,
  );

  return {
    ...updatedSurvey,
    ...skipLogicUpdates,
  };
};

const updateAnswer = (
  answerUUID: string,
  updates: Partial<Answer>,
  survey: Survey,
) => ({
  answers: {
    ...survey.answers,
    [answerUUID]: {
      ...survey.answers[answerUUID],
      ...updates,
      errors: {},
    },
  },
});

export const AnswerUtilities = {
  add: addAnswer,
  addBatch: addAnswers,
  addOther: addOtherAnswer,
  move: moveAnswer,
  remove: removeAnswer,
  update: updateAnswer,
};
